import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import apiClient from '../utils/api';
import { consumePostAuthRedirect } from '../utils/postAuthRedirect';
import { getCountryFlag } from '../utils/countryFlags';
import { getTeamLogoUrl } from '../utils/logoUrl';

interface Team {
  id: string;
  name: string;
  shortName?: string;
  logoUrl?: string;
  countryCode?: string;
  competitionId?: string;
}

const SelectFavouriteTeam: React.FC = () => {
  const [teams, setTeams] = useState<Team[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [selectedTeam, setSelectedTeam] = useState<string | null>(null);
  const [error, setError] = useState('');
  
  const { user, updateUser } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchTeams = async () => {
      try {
        setLoading(true);
        const response = await apiClient.get('/teams');
        setTeams(response.data);
      } catch (err: any) {
        setError(err.response?.data?.message || 'Failed to load teams');
      } finally {
        setLoading(false);
      }
    };

    fetchTeams();
  }, []);

  const handleTeamSelect = (teamId: string | null) => {
    setSelectedTeam(teamId);
  };

  const handleSubmit = async () => {
    if (!selectedTeam) {
      setError('Please select a team or choose "None, I\'m a neutral"');
      return;
    }

    try {
      setSubmitting(true);
      setError('');

      // Update user's favourite team
      const response = await apiClient.put('/users/favourite-team', {
        favouriteTeamId: selectedTeam === 'none' ? null : selectedTeam
      });

      // Update the user context with new data
      if (updateUser) {
        updateUser(response.data.user);
      }

      // Show brief confirmation
      const selectedTeamData = teams.find(team => team.id === selectedTeam);
      const teamName = selectedTeam === 'none' ? 'No team selected' : selectedTeamData?.name || 'Unknown team';
      
      // Check if user came from a fan league and should be redirected there
      const fanleagueGroupname = sessionStorage.getItem('fanleague_groupname');
      if (fanleagueGroupname) {
        try {
          // Join the fan league if not already a member
          const joinResponse = await apiClient.post(`/leagues/join-fanleague/${fanleagueGroupname}`);
          
          // Clear the league cache so the joined league appears immediately in the leagues list
          sessionStorage.removeItem('leagues_data');
          sessionStorage.removeItem('user_leagues_data');
          sessionStorage.removeItem('leagues_cache_timestamp');
          
          // Clear the sessionStorage after successful join
          sessionStorage.removeItem('fanleague_groupname');
          // Navigate to the league detail page
          navigate(`/leagues/${joinResponse.data.league.id}`, { 
            state: { 
              message: `Great! You've selected ${teamName} as your favourite team and joined the fan league.` 
            } 
          });
        } catch (joinError: any) {
          console.error('Failed to join fan league:', joinError);
          // If fan league join fails, navigate to new user page
          navigate('/new-user', { 
            state: { 
              message: `Great! You've selected ${teamName} as your favourite team.` 
            } 
          });
        }
      } else {
        // Send them on to whatever they were doing before signing up (e.g. creating a friends league)
        const redirect = consumePostAuthRedirect();
        if (redirect) {
          navigate(redirect.path, { state: redirect.state });
          return;
        }
        // Navigate to new user page (not fan league user)
        navigate('/new-user', { 
          state: { 
            message: `Great! You've selected ${teamName} as your favourite team.` 
          } 
        });
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save favourite team');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center" style={{ backgroundColor: 'var(--bg-primary)' }}>
        <div className="text-xl" style={{ color: 'var(--text-primary)' }}>Loading teams...</div>
      </div>
    );
  }

  if (error && !teams.length) {
    return (
      <div className="flex items-center justify-center" style={{ backgroundColor: 'var(--bg-primary)' }}>
        <div className="text-red-600 text-xl">{error}</div>
      </div>
    );
  }

  return (
          <div style={{ backgroundColor: 'var(--bg-primary)' }}>
        <div className="max-w-[95vw] md:max-w-7xl mx-auto py-6 sm:px-2 md:px-4 lg:px-8">
        {/* Header */}
        <div className="mb-8 text-center px-4">
          <h1 className="text-3xl font-bold mb-4" style={{ color: 'var(--text-primary)' }}>
            Choose Your Favourite Team
          </h1>
          <p className="text-lg" style={{ color: 'var(--text-secondary)' }}>
            Select your favourite team to personalize your experience
          </p>
        </div>

        {error && (
          <div className="mb-6 border px-4 py-3 rounded" style={{ backgroundColor: 'rgba(239, 68, 68, 0.1)', borderColor: '#ef4444', color: '#ef4444' }}>
            {error}
          </div>
        )}



        {/* Teams Carousel */}
        <div className="mb-8">
          

          {/* Teams Scrollable Carousel */}
          <div className="relative">
            {/* Navigation Arrows */}
            <button
              onClick={() => {
                const container = document.getElementById('teams-carousel');
                if (container) {
                  container.scrollBy({ left: -300, behavior: 'smooth' });
                }
              }}
              className="max-sm:-translate-x-3/4 absolute left-4 top-1/2 transform -translate-y-1/2 z-10 rounded-full p-3 shadow-lg transition-all duration-200 hover:scale-110"
              style={{ 
                backgroundColor: 'var(--bg-secondary)',
                color: 'var(--text-primary)',
                border: '1px solid var(--border-color, rgba(255, 255, 255, 0.1))',
                marginTop: '-20px'
              }}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            
            <button
              onClick={() => {
                const container = document.getElementById('teams-carousel');
                if (container) {
                  container.scrollBy({ left: 300, behavior: 'smooth' });
                }
              }}
              className="max-sm:translate-x-3/4 absolute right-4 top-1/2 transform -translate-y-1/2 z-10 rounded-full p-3 shadow-lg transition-all duration-200 hover:scale-110"
              style={{ 
                backgroundColor: 'var(--bg-secondary)',
                color: 'var(--text-primary)',
                border: '1px solid var(--border-color, rgba(255, 255, 255, 0.1))',
                marginTop: '-20px'
              }}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>

            <div 
              id="teams-carousel"
              className="flex overflow-x-auto gap-4 pb-4 scrollbar-hide px-8"
            >
              {/* Neutral Option - First Item */}
              <div
                className={`card mt-2 rounded-lg shadow-md hover:shadow-lg transition-all duration-200 cursor-pointer p-4 text-center flex-shrink-0 max-w-[128px] md:max-w-[160px]  ${
                  selectedTeam === 'none' ? 'ring-2 ring-green-500' : ''
                }`}
                style={{ 
                  backgroundColor: 'var(--bg-secondary)',
                  minWidth: '140px'
                }}
                onClick={() => handleTeamSelect('none')}
              >
                <div className="flex items-center justify-center mb-3">
                  <div className="w-12 h-12 flex items-center justify-center text-2xl"><br />
                    🤝
                  </div>
                </div>
                <h3 className="font-semibold text-sm" style={{ color: 'var(--text-primary)' }}><br />
                  None, I'm a neutral
                </h3>
              </div>

              {/* All Teams */}
              {teams.map(team => (
                <div
                  key={team.id}
                  className={`card mt-2 rounded-lg shadow-md hover:shadow-lg transition-all duration-200 cursor-pointer p-4 text-center flex-shrink-0 max-w-[128px] md:max-w-[160px] ${
                    selectedTeam === team.id ? 'ring-2 ring-green-500' : ''
                  }`}
                  style={{ 
                    backgroundColor: 'var(--bg-secondary)',
                    minWidth: '128px'
                  }}
                  onClick={() => handleTeamSelect(team.id)}
                >
                  <div className="flex items-center justify-center pt-4 mb-3">
                    {team.logoUrl ? (
                      <img 
                        src={getTeamLogoUrl(team.logoUrl)} 
                        alt={team.name} 
                        className="w-12 h-12 object-contain"
                      />
                    ) : (
                      <div className="w-12 h-12 flex items-center justify-center text-2xl">
                        {getCountryFlag(team.countryCode || 'EN')}
                      </div>
                    )}
                  </div>
                  <h3 className="font-semibold text-sm" style={{ color: 'var(--text-primary)' }}>
                    {team.name}
                  </h3>
                </div>
              ))}
            </div>
            
            {/* Scroll indicator */}
            <div className="text-center mt-2">
              <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                ← Use arrows or scroll to see more teams →
              </p>
            </div>
          </div>

        </div>


        {/* Submit Button */}
        <div className="text-center mt-8">
          <button
            onClick={handleSubmit}
            disabled={submitting || !selectedTeam}
            className={`px-8 py-3 rounded-lg font-medium transition-colors duration-200 ${
              submitting || !selectedTeam
                ? 'opacity-40 cursor-not-allowed'
                : 'hover:opacity-90'
            }`}
            style={{ backgroundColor: 'var(--highlight)', color: 'black' }}
          >
            {submitting ? 'Saving...' : 'Save Favourite Team and Continue'}
          </button>
        </div>


      </div>
    </div>
  );
};

export default SelectFavouriteTeam; 